import { Component } from '@verndale/core';
import { nanoid } from 'nanoid';
import scrollIntoView from 'scroll-into-view-if-needed';

import keys from '../utils/keys';
import { transitionComplete } from '../utils/timedEvents';
import { triggerCustomEvent } from '../utils/events';

const CLEAR_TIME = 500;

class Dropdown extends Component {
  setupDefaults() {
    this.dom = {
      $hiddenLabel: this.el._$('.dropdown__hidden-label'),
      $label: this.el._$('.dropdown__label'),
      $button: this.el._$('.dropdown__button'),
      $listbox: this.el._$('.dropdown__listbox'),
      $options: this.el._$$('.dropdown__option'),
      $selectedOptionLabel: this.el._$('.dropdown__selected-option-label'),
      $wrapper: this.el._$('.dropdown__wrapper'),
      $input: this.el._$('.field__input')
    };

    this.addAriaSelected();
    this.addDefaultLabel();
    this.addIds();
    this.keysSoFar = '';
  }

  addListeners() {
    const { $button, $listbox, $options } = this.dom;

    $button._on('click', ::this.handleButtonClick);
    $button._on('blur', ::this.handleButtonBlur);
    $listbox._on('keydown', ::this.handleListboxKeyboard);
    $options._on('click', ::this.handleListboxClick);
    document._on('mousedown', ::this.handleWindowClick);
  }

  addDefaultLabel() {
    const { $button } = this.dom;

    $button.dataset.defaultLabel = $button.textContent.trim();
  }

  addAriaSelected() {
    const { $options } = this.dom;

    $options.forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  addIds() {
    const { $hiddenLabel, $button, $listbox, $options, $label } = this.dom;

    const buttonId = $button.id;
    const hiddenLabelId = `id-${nanoid()}`;
    const labelId = `id-${nanoid()}`;

    if ($hiddenLabel) {
      $hiddenLabel.id = hiddenLabelId;
      $button.setAttribute('aria-labelledby', `${hiddenLabelId} ${buttonId}`);
      $listbox.setAttribute('aria-labelledby', hiddenLabelId);
    }

    if ($label) {
      $label.id = labelId;
      $listbox.setAttribute('aria-labelledby', labelId);
    }

    $options.forEach((option) => (option.id = nanoid()));
  }

  handleButtonBlur() {
    if (this.isField()) {
      this.el.classList.add('field--touched');
    }
  }

  handleButtonClick() {
    const { $listbox } = this.dom;

    if (!this.isListboxOpen()) {
      this.openListbox();
      transitionComplete(() => $listbox.focus());
    } else {
      this.closeListbox();
    }
  }

  handleListboxKeyboard(e) {
    e.preventDefault();

    const { $button, $options } = this.dom;
    const { length } = $options;

    switch (e.keyCode) {
      case keys.space:
      case keys.enter:
        this.closeListbox();
        $options[this.getSelectedOptionIndex()]._$('a')?.click();
        $button.focus();
        break;
      case keys.escape:
        e.stopPropagation();
        this.closeListbox();
        $button.focus();
        break;
      case keys.home:
        this.setSelectedOption($options[0]);
        break;
      case keys.end:
        this.setSelectedOption($options[$options.length - 1]);
        break;
      case keys.down:
        this.setSelectedOption(
          $options[(this.getSelectedOptionIndex() + 1) % length]
        );
        break;
      case keys.up:
        this.setSelectedOption(
          $options[(this.getSelectedOptionIndex() - 1 + length) % length]
        );
        break;
      default:
        this.keysSoFar += e.key;
        if (this.keysSoFar) {
          this.searchOptionToFocus();
        }
        this.clearKeysSoFar();
        break;
    }
  }

  handleListboxClick(e) {
    const { $listbox, $options, $selectedOptionLabel } = this.dom;
    $selectedOptionLabel.textContent = e.target.textContent;
    $options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    e.currentTarget.setAttribute('aria-selected', 'true');
    $listbox.setAttribute('aria-activedescendant', e.currentTarget.id);
    this.triggerValueChangeEvent(e.currentTarget.dataset.value);
    this.closeListbox();
  }

  handleWindowClick(e) {
    const isOutside = !e.target.closest('.dropdown__wrapper');

    if (isOutside) {
      this.closeListbox();
    }
  }

  setSelectedOption(option) {
    const { $listbox, $selectedOptionLabel, $options } = this.dom;

    $options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    option.focus();
    scrollIntoView(option, {
      behavior: 'smooth',
      scrollMode: 'if-needed',
      block: 'nearest'
    });
    $listbox.setAttribute('aria-activedescendant', option.id);
    this.triggerValueChangeEvent(option.dataset.value);
    $selectedOptionLabel.textContent = option.textContent;
  }

  getSelectedOptionIndex() {
    const { $options } = this.dom;

    return $options.findIndex(
      (option) => option.getAttribute('aria-selected') === 'true'
    );
  }

  clearKeysSoFar() {
    setTimeout(() => (this.keysSoFar = ''), CLEAR_TIME);
  }

  searchOptionToFocus() {
    const { $options } = this.dom;
    const indexToFocus = $options.findIndex((option) =>
      option.textContent.trim().toLowerCase().startsWith(this.keysSoFar)
    );

    if (indexToFocus >= 0) {
      this.setSelectedOption($options[indexToFocus]);
    }
  }

  openListbox() {
    const { $button, $wrapper } = this.dom;

    $button.setAttribute('aria-expanded', 'true');
    $wrapper.classList.add('dropdown__wrapper--open');
  }

  closeListbox() {
    const { $button, $wrapper } = this.dom;

    $button.setAttribute('aria-expanded', 'false');
    $wrapper.classList.remove('dropdown__wrapper--open');
  }

  isListboxOpen() {
    const { $button } = this.dom;

    return $button.getAttribute('aria-expanded') === 'true';
  }

  isField() {
    return this.el.classList.contains('field');
  }

  triggerValueChangeEvent(value) {
    const { $input, $listbox } = this.dom;

    if (value) {
      $listbox.dataset.value = value;
      triggerCustomEvent($listbox, 'valueChange', {
        detail: { value }
      });

      if ($input) {
        $input.value = value;
        triggerCustomEvent($input, 'change', { bubbles: true });
      }
    }
  }
}

export default Dropdown;
